import {EMPTY_MOTION_IMAGE} from '../../protocol/cinematic-motion';
import {mediaParserController} from '@remotion/media-parser';
import {parseMediaOnWebWorker} from '@remotion/media-parser/worker';

type Dimensions={width:number;height:number;durationInSeconds?:number|null};
type VideoProbe=(url:string)=>Promise<Dimensions|null>;
export function boundedMediaProbe<T>(probe:Promise<T>,timeoutMs=30000):Promise<T>{
 let timer:ReturnType<typeof setTimeout>;
 const timeout=new Promise<never>((_,reject)=>{timer=setTimeout(()=>reject(new Error('원본 확인 시간이 초과됐습니다. 다시 시도해 주세요.')),timeoutMs);});
 return Promise.race([probe,timeout]).finally(()=>clearTimeout(timer));
}
async function readVideoDimensions(url:string):Promise<Dimensions|null>{
  const controller=mediaParserController();
  const timer=setTimeout(()=>controller.abort(new Error('원본 확인 시간이 초과됐습니다.')),30000);
  try{
    const result=await parseMediaOnWebWorker({src:url,fields:{dimensions:true,durationInSeconds:true},controller,acknowledgeRemotionLicense:true});
    return result.dimensions?{...result.dimensions,durationInSeconds:result.durationInSeconds}:null;
  }finally{clearTimeout(timer);}
}

/** Keep probing outside React modules so refresh does not invalidate the app root.
 * Container metadata does not require native video playback or replace the persisted source URL.
 */
export function measureMedia(
  type: string,
  url: string,
  videoProbe:VideoProbe=readVideoDimensions,
): Promise<{ width: number; height: number;durationInSeconds?:number|null }> {
  return new Promise((resolve, reject) => {
    if(type==="image"&&url===EMPTY_MOTION_IMAGE){resolve({width:1,height:1});return;}
    const source = new URL(url);
    const embeddedImage = type === "image" && url.length <= 2800000 && /^data:image\/(?:png|jpeg|webp|svg\+xml);base64,[A-Za-z0-9+/]{32,}={0,2}$/.test(url);
    if ((!embeddedImage && source.protocol !== "https:") || source.username || source.password) {
      reject(new Error("HTTPS 원본만 확인할 수 있습니다."));
      return;
    }
    if(type==='video'){
      void boundedMediaProbe(videoProbe(source.href)).then(dimensions=>{
        if(dimensions&&Number.isFinite(dimensions.width)&&Number.isFinite(dimensions.height)&&dimensions.width>0&&dimensions.height>0)resolve(dimensions);
        else reject(new Error('원본 크기를 확인하지 못했습니다.'));
      },reject);
      return;
    }
    if(type!=='image'){reject(new Error('지원하지 않는 원본 종류입니다.'));return;}
    const media = new Image();
    // Use the same anonymous CORS mode as the editor so an earlier metadata request
    // cannot poison the browser cache with a response lacking ACAO headers.
    media.crossOrigin = "anonymous";
    const cleanup = () => {
      clearTimeout(timer);
      media.onload = null;
      media.onerror = null;
      media.removeAttribute("src");
    };
    const done = () => {
      const width = media.naturalWidth;
      const height = media.naturalHeight;
      cleanup();
      if (width > 0 && height > 0) resolve({ width, height });
      else reject(new Error("원본 크기를 확인하지 못했습니다."));
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("원본 확인 시간이 초과됐습니다."));
    }, 30000);
    media.onerror = () => {
      cleanup();
      reject(new Error('원본 이미지를 열지 못했습니다.'));
    };
    media.onload = done;
    media.src = source.href;
  });
}
